import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, growthMarketplaceTopic } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import { GrowthAdminService, GrowthAgentInstallResponse } from './growth-admin.service';
import {
  GROWTH_AGENT_RECOMMENDATION_TYPES,
  growthAgentPermissionManifestFields,
  mergePermissionManifestJson,
  parseAllowedRecommendationTypes
} from './growth-agent-marketplace-form.models';

@Component({
  standalone: true,
  selector: 'ain-growth-agent-marketplace-install-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    StructuredJsonFormComponent,
    RouterLink,
    FormsModule,
    ButtonModule,
    InputTextModule,
    CheckboxModule,
    MultiSelectModule
  ],
  template: `
    <ain-page [title]="install()?.displayName ?? t('growth.agentMarketplace.installDetail.title')" [description]="t('growth.agentMarketplace.installDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.agentMarketplace.installDetail.help.title'"
        [descriptionKey]="'growth.agentMarketplace.installDetail.help.description'"
        [bulletKeys]="['growth.agentMarketplace.installDetail.help.bullets.permissions', 'growth.agentMarketplace.installDetail.help.bullets.killSwitch']" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (!install()) {
        <ain-state type="empty" [title]="t('growth.agentMarketplace.installDetail.messages.notFound')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/growth/marketplace/agent-installs']">{{ t('catalog.action.backToList') }}</a>
          <div class="toolbar-actions">
            @if (canDisable()) {
              <button
                pButton
                type="button"
                severity="danger"
                [label]="t('growth.agentMarketplace.installs.disable')"
                [loading]="actionType() === 'disable'"
                (click)="disable()"></button>
            }
          </div>
        </div>

        <section class="card">
          <h3>{{ t('growth.agentMarketplace.installDetail.sections.metadata') }}</h3>
          <ain-info-hint
            [titleKey]="'growth.agentMarketplace.installDetail.help.metadata.title'"
            [descriptionKey]="'growth.agentMarketplace.installDetail.help.metadata.description'" />
          <div class="grid">
            <label>
              <span>{{ t('growth.agentMarketplace.installs.columns.listingKey') }}</span>
              <input pInputText type="text" [ngModel]="install()!.listingKey" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.agentMarketplace.installs.columns.displayName') }}</span>
              <input pInputText type="text" [ngModel]="install()!.displayName" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.agentMarketplace.installDetail.fields.agentDefinitionKey') }}</span>
              <input pInputText type="text" [ngModel]="install()!.agentDefinitionKey" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.agentMarketplace.installs.columns.version') }}</span>
              <input pInputText type="text" [ngModel]="install()!.installedVersion ?? install()!.version" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.agentMarketplace.installs.columns.industryPackKey') }}</span>
              <input pInputText type="text" [ngModel]="install()!.industryPackKey ?? '-'" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.agentMarketplace.installs.columns.lifecycleStatus') }}</span>
              <input pInputText type="text" [ngModel]="lifecycleLabel(install()!.lifecycleStatus)" [disabled]="true" />
            </label>
          </div>
        </section>

        <section class="card">
          <h3>{{ t('growth.agentMarketplace.installDetail.sections.permissions') }}</h3>
          <ain-info-hint
            [titleKey]="'growth.agentMarketplace.permissions.help.title'"
            [descriptionKey]="'growth.agentMarketplace.permissions.help.description'"
            [bulletKeys]="['growth.agentMarketplace.permissions.help.bullets.recommendationTypes']" />
          <ain-structured-json-form [fields]="permissionFields" [(payloadJson)]="permissionPayloadJson" />
          <ain-form-field [label]="t('growth.agentMarketplace.permissions.fields.allowedRecommendationTypes')">
            <p-multiSelect
              [options]="recommendationTypeOptions()"
              [(ngModel)]="allowedRecommendationTypes"
              optionLabel="label"
              optionValue="value"
              appendTo="body"
              [placeholder]="t('growth.agentMarketplace.permissions.fields.allowedRecommendationTypesPlaceholder')" />
          </ain-form-field>
          <button
            pButton
            type="button"
            [label]="t('growth.agentMarketplace.permissions.actions.save')"
            [loading]="actionType() === 'permissions'"
            (click)="savePermissions()"></button>
        </section>

        <section class="card">
          <h3>{{ t('growth.agentMarketplace.killSwitch.title') }}</h3>
          <ain-info-hint
            [titleKey]="'growth.agentMarketplace.killSwitch.help.title'"
            [descriptionKey]="'growth.agentMarketplace.killSwitch.help.description'"
            [bulletKeys]="['growth.agentMarketplace.killSwitch.help.bullets.blockRun']" />
          <div class="kill-switch-row">
            <p-checkbox
              [binary]="true"
              [(ngModel)]="killSwitchEnabled"
              [inputId]="'install-kill-switch'" />
            <label [for]="'install-kill-switch'">{{ t('growth.agentMarketplace.killSwitch.fields.enabled') }}</label>
          </div>
          <ain-form-field [label]="t('growth.agentMarketplace.killSwitch.fields.reason')">
            <input pInputText type="text" [(ngModel)]="killSwitchReason" />
          </ain-form-field>
          <button
            pButton
            type="button"
            severity="danger"
            [label]="t('growth.agentMarketplace.killSwitch.actions.save')"
            [loading]="actionType() === 'killSwitch'"
            (click)="saveKillSwitch()"></button>
        </section>

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
    .toolbar-actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .card h3 { margin: 0; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    .kill-switch-row { display: flex; align-items: center; gap: 8px; }
    .message.error { color: #b91c1c; }
    @media (max-width: 960px) { .grid { grid-template-columns: 1fr 1fr; } }
    @media (max-width: 640px) { .grid { grid-template-columns: 1fr; } }
  `]
})
export class GrowthAgentMarketplaceInstallDetailComponent implements OnInit {
  private readonly api = inject(GrowthAdminService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly install = signal<GrowthAgentInstallResponse | null>(null);
  readonly errorMessage = signal('');
  readonly actionType = signal<'disable' | 'permissions' | 'killSwitch' | null>(null);

  readonly permissionFields = growthAgentPermissionManifestFields;

  permissionPayloadJson = '{}';
  allowedRecommendationTypes: string[] = [];
  killSwitchEnabled = false;
  killSwitchReason = '';

  private installId = '';
  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.installId = this.route.snapshot.paramMap.get('installId') ?? '';

    this.liveUpdateService.events$
      .pipe(
        filter(event => {
          if (event.eventType !== LiveUpdateEventTypes.growthMarketplaceChanged) {
            return false;
          }
          const payloadInstallId = event.payload['installId'];
          return payloadInstallId === undefined || String(payloadInstallId).toLowerCase() === this.installId.toLowerCase();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.load(false));

    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();
    this.load(true);
  }

  recommendationTypeOptions(): Array<{ label: string; value: string }> {
    return GROWTH_AGENT_RECOMMENDATION_TYPES.map(value => ({
      value,
      label: this.t(`growth.agentMarketplace.permissions.recommendationTypes.${value}`)
    }));
  }

  lifecycleLabel(value: string): string {
    return this.t(`growth.agentMarketplace.lifecycle.${value}`);
  }

  canDisable(): boolean {
    return this.install()?.lifecycleStatus === 'Installed';
  }

  disable(): void {
    this.runAction('disable', () => this.api.disableAgentMarketplaceInstall(this.installId));
  }

  savePermissions(): void {
    const manifestJson = mergePermissionManifestJson(this.permissionPayloadJson, this.allowedRecommendationTypes);
    this.runAction('permissions', () => this.api.updateAgentMarketplaceInstallPermissions(this.installId, manifestJson));
  }

  saveKillSwitch(): void {
    this.runAction('killSwitch', () => this.api.setAgentMarketplaceInstallKillSwitch(
      this.installId,
      this.killSwitchEnabled,
      this.killSwitchReason.trim() || null
    ));
  }

  load(showLoading = true): void {
    if (!this.installId) {
      this.loading.set(false);
      this.install.set(null);
      return;
    }
    if (showLoading) {
      this.loading.set(true);
    }
    this.errorMessage.set('');
    this.api.getAgentMarketplaceInstall(this.installId).subscribe({
      next: row => {
        this.install.set(row);
        this.permissionPayloadJson = row.permissionManifestJson;
        this.allowedRecommendationTypes = parseAllowedRecommendationTypes(row.permissionManifestJson);
        this.killSwitchEnabled = row.killSwitchEnabled;
        this.killSwitchReason = row.killSwitchReason ?? '';
        this.loading.set(false);
      },
      error: () => {
        this.install.set(null);
        this.loading.set(false);
        this.errorMessage.set(this.t('growth.agentMarketplace.installDetail.messages.loadFailed'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private runAction(
    type: 'disable' | 'permissions' | 'killSwitch',
    request: () => ReturnType<GrowthAdminService['disableAgentMarketplaceInstall']>
  ): void {
    this.actionType.set(type);
    this.errorMessage.set('');
    request().pipe(finalize(() => this.actionType.set(null))).subscribe({
      next: row => {
        this.install.set(row);
        this.permissionPayloadJson = row.permissionManifestJson;
        this.allowedRecommendationTypes = parseAllowedRecommendationTypes(row.permissionManifestJson);
        this.killSwitchEnabled = row.killSwitchEnabled;
        this.killSwitchReason = row.killSwitchReason ?? '';
      },
      error: () => this.errorMessage.set(this.t(`growth.agentMarketplace.installDetail.messages.${type}Failed`))
    });
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthMarketplaceTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
